import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { getMyPermissions } from '../api/permissions.api';
import { queryKeys } from '../api/queryKeys';
import { isAdmin } from '../helpers/appType';
import { UserPermission } from '../types/permissions.types';

/**
 * Fine-grained permissions for the logged in user.
 *
 * Modelled on `useVerificationsCount`, but deliberately without a
 * `refetchInterval` - a user's permissions do not change mid-session, and a
 * grant only needs to take effect on their next login.
 *
 * Three states matter to callers, and conflating them causes real bugs:
 *
 *   - **in flight** (`isLoading`) - the answer is not known yet. Route guards
 *     must render nothing rather than redirect, or a deep link to a gated page
 *     bounces before the response lands.
 *   - **loaded** - `can()` is authoritative.
 *   - **failed** - `can()` returns false and `isLoading` is false, i.e. denied.
 *     Treating a failure as "still loading" would leave a blank screen forever
 *     after a single 500.
 *
 * Only the admin API exposes this endpoint, so the query is disabled elsewhere
 * (a disabled query is never `isLoading`, so it reads as a clean deny).
 */
export function usePermissions() {
  const query = useQuery({
    queryKey: queryKeys.permissions.me,
    queryFn: async () => {
      const { data } = await getMyPermissions();
      return data.data;
    },
    enabled: isAdmin,
    staleTime: Infinity,
  });

  const data = query.data;

  const can = useCallback(
    (permission: UserPermission) => {
      if (!data) return false;
      // `admin` holds everything implicitly; the list may well be empty.
      return data.allPermissions || data.permissions.includes(permission);
    },
    [data]
  );

  return {
    can,
    /** True only while the first request is in flight - never on failure. */
    isLoading: query.isLoading,
    allPermissions: data?.allPermissions ?? false,
    permissions: data?.permissions ?? [],
  };
}
